/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from "react";
import ConstraintCard from "./ConstraintCard";
import ExpressionRowBuilder, { buildExpression, NUMERIC_TYPES } from "./ExpressionRowBuilder";

const UNARY_OPS  = ["IS NOT NULL", "IS NULL", "IS TRUE", "IS FALSE"];
// longest / more-specific operators first so prefix matching is unambiguous
const BINARY_OPS = ["!=", ">=", "<=", "!~", "NOT LIKE", "LIKE", "=", ">", "<", "~"];

function stripQuotes(v) {
  v = (v || "").trim();
  if (v.length >= 2 && v.startsWith("'") && v.endsWith("'")) return v.slice(1, -1);
  return v;
}

// split a comma-separated list, respecting single-quoted segments
function splitList(s) {
  const out = [];
  let cur = "", inQ = false;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "'") { inQ = !inQ; cur += ch; }
    else if (ch === "," && !inQ) { out.push(cur); cur = ""; }
    else cur += ch;
  }
  if (cur.trim()) out.push(cur);
  return out.map(stripQuotes);
}

// parse a single clause like  "col" = 'val'  into a condition object
function parseClause(clause) {
  const m = (clause || "").match(/^\s*"([^"]+)"\s*([\s\S]*)$/);
  if (!m) return null;
  const column = m[1];
  const rest = m[2].trim();

  for (const u of UNARY_OPS) {
    if (rest === u) return { column, operator: u, val: "", val2: "", vals: [], valKind: "literal" };
  }
  const bm = rest.match(/^BETWEEN\s+([\s\S]+?)\s+AND\s+([\s\S]+)$/);
  if (bm) return { column, operator: "BETWEEN", val: bm[1].trim(), val2: bm[2].trim(), vals: [], valKind: "literal" };

  const inm = rest.match(/^(NOT IN|IN)\s*\(([\s\S]*)\)\s*$/);
  if (inm) return { column, operator: inm[1], val: "", val2: "", vals: splitList(inm[2]), valKind: "literal" };

  const COMPARISON_OPS = ["!=", ">=", "<=", "=", ">", "<"];
  for (const op of BINARY_OPS) {
    if (rest.startsWith(op)) {
      const rhs = rest.slice(op.length).trim();
      const colRef = COMPARISON_OPS.includes(op) && rhs.match(/^"([^"]+)"$/);
      if (colRef) {
        return { column, operator: op, val: colRef[1], val2: "", vals: [], valKind: "column" };
      }
      return { column, operator: op, val: stripQuotes(rhs), val2: "", vals: [], valKind: "literal" };
    }
  }
  return null;
}

// parse a compiled "x AND y ..." OR "x OR y ..." expression back into builder
// rows. The constraint uses a single uniform connector (whole-constraint
// toggle), so a string mixing top-level AND and OR is not round-trippable —
// return null so the caller falls back to a raw single-row display.
export function parseExpression(exprStr, columns) {
  const s = exprStr || "";
  // lookahead for a following quoted identifier so BETWEEN's inner AND (which
  // is followed by a value, not an identifier) is never treated as a connector.
  const hasTopAnd = /\s+AND\s+(?=")/.test(s);
  const hasTopOr  = /\s+OR\s+(?=")/.test(s);
  if (hasTopAnd && hasTopOr) return null;
  const connector = hasTopOr ? "OR" : "AND";
  const splitRe = new RegExp(`\\s+${connector}\\s+(?=")`);
  const clauses = s.split(splitRe);
  const conditions = [];
  const expressions = [];
  for (const clause of clauses) {
    const cond = parseClause(clause);
    if (!cond) return null;
    const colDef = columns.find(c => c.name === cond.column);
    const isNumeric = NUMERIC_TYPES.includes(colDef?.data_type || "");
    conditions.push(cond);
    expressions.push(buildExpression(cond.column, cond.operator, cond.val, cond.val2, cond.vals, isNumeric, cond.valKind));
  }
  return conditions.length ? { conditions, expressions, connector } : null;
}

/**
 * Props:
 *   value            — array of { name, expression }
 *   onChange         — (newArray) => void
 *   availableColumns — array of { name, data_type }
 *   hasRows          — boolean; when true, add/edit disabled (remove-only)
 *   onEditingChange  — (bool) => void
 */
export default function CheckConstraintBuilder({ value = [], onChange, availableColumns = [], hasRows = false, onEditingChange }) {
  // editing = { index, data: { name, conditions: [], expressions: [] } } | null
  const [editing, setEditing] = useState(null);

  useEffect(() => { onEditingChange && onEditingChange(editing !== null); }, [editing, onEditingChange]);

  const emptyCond = () => ({ column: "", operator: "", val: "", val2: "", vals: [], valKind: "literal" });
  const exprOf = (d) => (d.expressions || []).filter(Boolean).join(` ${d.connector || "AND"} `);
  const valid = editing && editing.data.name.trim() && (editing.data.label || "").trim() && exprOf(editing.data) !== "";
  const saveHint = "Provide a name, a display label, and at least one complete condition.";

  function startNew() { setEditing({ index: "new", data: { name: "", label: "", conditions: [emptyCond()], expressions: [""], connector: "AND" } }); }
  function startEdit(idx) {
    // existing rows carry only a compiled expression string — parse it back into
    // editable condition rows; fall back to a single empty row if it can't be parsed.
    const parsed = parseExpression(value[idx].expression, availableColumns);
    const data = parsed
      ? { name: value[idx].name, label: value[idx].label || "", conditions: parsed.conditions, expressions: parsed.expressions, connector: parsed.connector }
      : { name: value[idx].name, label: value[idx].label || "", conditions: [emptyCond()], expressions: [value[idx].expression], connector: "AND" };
    setEditing({ index: idx, data });
  }
  function cancel() { setEditing(null); }
  function remove(idx) { onChange(value.filter((_, i) => i !== idx)); }
  function patchName(name) { setEditing(e => ({ ...e, data: { ...e.data, name } })); }
  function patchLabel(label) { setEditing(e => ({ ...e, data: { ...e.data, label } })); }
  function patchConnector(connector) { setEditing(e => ({ ...e, data: { ...e.data, connector } })); }
  function addCondition() {
    setEditing(e => ({ ...e, data: { ...e.data, conditions: [...e.data.conditions, emptyCond()], expressions: [...e.data.expressions, ""] } }));
  }
  function removeCondition(rowIdx) {
    setEditing(e => ({ ...e, data: {
      ...e.data,
      conditions: e.data.conditions.filter((_, j) => j !== rowIdx),
      expressions: e.data.expressions.filter((_, j) => j !== rowIdx),
    } }));
  }
  function updateCondition(rowIdx, newVal, expr) {
    setEditing(e => ({ ...e, data: {
      ...e.data,
      conditions: e.data.conditions.map((c, j) => j === rowIdx ? newVal : c),
      expressions: e.data.expressions.map((x, j) => j === rowIdx ? expr : x),
    } }));
  }
  function save() {
    if (!valid) return;
    const row = { name: editing.data.name.trim(), label: (editing.data.label || "").trim(), expression: exprOf(editing.data) };
    if (editing.index === "new") onChange([...value, row]);
    else onChange(value.map((r, i) => i === editing.index ? row : r));
    setEditing(null);
  }

  const editorBody = editing && (
    <>
      <div className="mb-2">
        <label style={{ fontSize: 12, color: "#555" }}>Constraint name (pg identifier)</label>
        <input className="form-control" placeholder="e.g. chk_age_positive" value={editing.data.name}
          onChange={e => patchName(e.target.value)} style={{ fontSize: 13 }} />
      </div>
      <div className="mb-2">
        <label style={{ fontSize: 12, color: "#555" }}>Display label (shown to users on violation) <span style={{ color: "#dc3545" }}>*</span></label>
        <input className="form-control" placeholder="e.g. End date must be after start date" value={editing.data.label}
          onChange={e => patchLabel(e.target.value)} style={{ fontSize: 13 }} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <label style={{ fontSize: 12, color: "#555", margin: 0 }}>Conditions</label>
        <span style={{ fontSize: 11, color: "#888" }}>Join with:</span>
        <div style={{ display: "flex" }}>
          {["AND", "OR"].map((c, i) => (
            <button key={c} type="button" onClick={() => patchConnector(c)}
              style={{
                fontSize: 12, padding: "2px 12px", cursor: "pointer",
                border: "1px solid #0d6efd",
                borderTopLeftRadius: i === 0 ? 6 : 0, borderBottomLeftRadius: i === 0 ? 6 : 0,
                borderTopRightRadius: i === 1 ? 6 : 0, borderBottomRightRadius: i === 1 ? 6 : 0,
                borderLeft: i === 1 ? "none" : "1px solid #0d6efd",
                background: (editing.data.connector || "AND") === c ? "#0d6efd" : "#fff",
                color: (editing.data.connector || "AND") === c ? "#fff" : "#0d6efd",
              }}>
              {c}
            </button>
          ))}
        </div>
      </div>
      {editing.data.conditions.map((cond, rowIdx) => (
        <ExpressionRowBuilder key={rowIdx} columns={availableColumns} value={cond}
          onChange={(newVal, expr) => updateCondition(rowIdx, newVal, expr)}
          onRemove={editing.data.conditions.length > 1 ? () => removeCondition(rowIdx) : undefined} />
      ))}
      <button type="button" onClick={addCondition}
        style={{ fontSize: 12, color: "#0d6efd", background: "none", border: "1px solid #0d6efd", borderRadius: 6, padding: "2px 10px", cursor: "pointer", marginTop: 8 }}>
        + Add condition
      </button>
    </>
  );

  return (
    <div>
      {value.map((c, idx) => (
        editing && editing.index === idx ? (
          <ConstraintCard key={idx} mode="edit" title="Edit Check Constraint"
            onCancel={cancel} onSave={save} saveDisabled={!valid} saveHint={saveHint}>
            {editorBody}
          </ConstraintCard>
        ) : (
          <ConstraintCard key={idx} mode="summary" title={c.label || c.name || "(unnamed)"}
            canEdit={!hasRows && !editing}
            onEdit={() => startEdit(idx)} onRemove={() => remove(idx)}
            summary={<code style={{ fontSize: 12, color: "#555", wordBreak: "break-all" }}>{c.expression}</code>} />
        )
      ))}

      {editing && editing.index === "new" && (
        <ConstraintCard mode="edit" title="New Check Constraint"
          onCancel={cancel} onSave={save} saveDisabled={!valid} saveHint={saveHint}>
          {editorBody}
        </ConstraintCard>
      )}

      {hasRows ? (
        <div style={{ fontSize: 12, color: "#9a6700", background: "#fff8e1", border: "1px solid #ffe39e", borderRadius: 6, padding: "6px 10px", marginTop: 4 }}>
          <i className="bi bi-info-circle" /> Cannot edit or add after creation, you can only remove the constraint.
        </div>
      ) : (
        !editing && (
          <button type="button" onClick={startNew}
            style={{ fontSize: 13, color: "#0d6efd", background: "none", border: "1px solid #0d6efd", borderRadius: 6, padding: "4px 14px", cursor: "pointer", marginTop: 4 }}>
            <i className="bi bi-plus-lg" /> Add Check Constraint
          </button>
        )
      )}
    </div>
  );
}
